import { DataGlueGraph, FunctionDefinition, FunctionPortDefinition, NodeInstance } from '@glueapp/graph-types';
import { ValidationError, ValidationRule, ValidationRuleResult } from '../ValidationRule';
import { DataType, getStringRepresentationFromType, typeAMatchesTypeB } from '@glueapp/graph-types';

export const FunctionInterfaceSynchronizationRule: ValidationRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Validate each function's interface nodes
    if (graph.functions) {
        for (const func of graph.functions) {
            validateFunctionInterface(func, errors);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function validateFunctionInterface(functionDef: FunctionDefinition, errors: ValidationError[]): void {
    // Validate function input node
    validateFunctionInputNode(functionDef, errors);

    // Validate function output node
    validateFunctionOutputNode(functionDef, errors);
}

function validateFunctionInputNode(functionDef: FunctionDefinition, errors: ValidationError[]): void {
    const inputNode = functionDef.nodeInstances.find(node => node.id === functionDef.functionInputsNodeId);

    if (!inputNode) {
        errors.push({
            code: 'MISSING_FUNCTION_INPUT_NODE',
            message: 'Function is missing its input interface node',
            severity: 'error',
            location: { functionId: functionDef.id },
        });
        return;
    }

    // Validate input node output ports match function input port definitions
    const expectedPorts = functionDef.inputPortDefinitions;
    const actualPorts = inputNode.outPorts;

    validatePortDefinitionMatch(expectedPorts, actualPorts, 'input', functionDef.id, inputNode.id, errors);

    // Validate that the input node has no input ports (except inherent)
    const nonInherentInputPorts = inputNode.inPorts.filter(port => port.name !== 'InherentIn');
    if (nonInherentInputPorts.length > 0) {
        errors.push({
            code: 'FUNCTION_INPUT_NODE_HAS_INPUT_PORTS',
            message: 'Function input node should not have input ports (except InherentIn)',
            severity: 'warning',
            location: {
                functionId: functionDef.id,
                nodeId: inputNode.id,
            },
        });
    }
}

function validateFunctionOutputNode(functionDef: FunctionDefinition, errors: ValidationError[]): void {
    const outputNode = functionDef.nodeInstances.find(node => node.id === functionDef.functionOutputsNodeId);

    if (!outputNode) {
        errors.push({
            code: 'MISSING_FUNCTION_OUTPUT_NODE',
            message: 'Function is missing its output interface node',
            severity: 'error',
            location: { functionId: functionDef.id },
        });
        return;
    }

    // Validate output node input ports match function output port definitions
    const expectedPorts = functionDef.outputPortDefinitions;
    const actualPorts = outputNode.inPorts;

    validatePortDefinitionMatch(expectedPorts, actualPorts, 'output', functionDef.id, outputNode.id, errors);

    // Validate that the output node has no output ports (except inherent)
    const nonInherentOutputPorts = outputNode.outPorts.filter(port => port.name !== 'InherentOut');
    if (nonInherentOutputPorts.length > 0) {
        errors.push({
            code: 'FUNCTION_OUTPUT_NODE_HAS_OUTPUT_PORTS',
            message: 'Function output node should not have output ports (except InherentOut)',
            severity: 'warning',
            location: {
                functionId: functionDef.id,
                nodeId: outputNode.id,
            },
        });
    }
}

function validatePortDefinitionMatch(
    expectedPorts: FunctionPortDefinition[],
    actualPorts: { name: string; type: DataType; optional?: boolean }[],
    portDirection: 'input' | 'output',
    functionId: string,
    nodeId: number,
    errors: ValidationError[],
): void {
    // Filter out inherent ports for comparison
    const filteredActualPorts = actualPorts.filter(port => !port.name.startsWith('Inherent'));

    if (expectedPorts.length !== filteredActualPorts.length) {
        errors.push({
            code: 'FUNCTION_INTERFACE_PORT_COUNT_MISMATCH',
            message: `Function ${portDirection} interface has ${filteredActualPorts.length} ports but definition specifies ${expectedPorts.length}`,
            severity: 'error',
            location: {
                functionId,
                nodeId,
                portDirection,
            },
        });
    }

    expectedPorts.forEach((expectedPort, index) => {
        const actualPort = filteredActualPorts[index];
        if (!actualPort) return;

        // Validate port name
        if (expectedPort.name !== actualPort.name) {
            errors.push({
                code: 'FUNCTION_INTERFACE_PORT_NAME_MISMATCH',
                message: `Expected ${portDirection} port '${expectedPort.name}' but interface node has '${actualPort.name}'`,
                severity: 'error',
                location: {
                    functionId,
                    nodeId,
                    portName: actualPort.name,
                    expectedPortName: expectedPort.name,
                },
            });
        }

        // Validate port optional flag
        if ((expectedPort.optional || false) !== (actualPort.optional || false)) {
            errors.push({
                code: 'FUNCTION_INTERFACE_PORT_OPTIONAL_MISMATCH',
                message: `Port '${actualPort.name}' optional flag mismatch: expected ${expectedPort.optional || false}, got ${actualPort.optional || false}`,
                severity: 'warning',
                location: {
                    functionId,
                    nodeId,
                    portName: actualPort.name,
                },
            });
        }

        // Type compatibility validation
        if (!typeAMatchesTypeB(actualPort.type, expectedPort.type)) {
            errors.push({
                code: 'FUNCTION_INTERFACE_PORT_TYPE_MISMATCH',
                message: `Function ${portDirection} port '${actualPort.name}' has incorrect type: expected ${getStringRepresentationFromType(expectedPort.type)}, got ${getStringRepresentationFromType(actualPort.type)}`,
                severity: 'error',
                location: {
                    functionId,
                    nodeId,
                    portName: actualPort.name,
                },
            });
        }
    });
}
